import { z } from 'zod';
import type { RefLabel } from '../services/productFees';
import { isRoutablePayerPair, type DefaultPayerPair } from '../utils/routablePayerPairs';

export function makePayerPairOverrideSchema(targets: RefLabel[], defaultPair: DefaultPayerPair | null) {
    const codeOf = (id: string) => targets.find((target) => target.id === id)?.code;

    return z
        .object({
            paid_by_id: z.string().uuid('Select who pays'),
            paid_to_id: z.string().uuid('Select who is paid'),
        })
        .superRefine((value, ctx) => {
            const by = codeOf(value.paid_by_id);
            const to = codeOf(value.paid_to_id);

            if (by === undefined || to === undefined) return;

            if (!isRoutablePayerPair(by, to)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['paid_to_id'],
                    message: `${by} to ${to} is not a payer pair the ledger can post.`,
                });
                return;
            }

            if (defaultPair && value.paid_by_id === defaultPair.paidById && value.paid_to_id === defaultPair.paidToId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['paid_to_id'],
                    message: 'This is the default payer pair; use the default pair instead.',
                });
            }
        });
}

export type PayerPairOverrideValues = z.infer<ReturnType<typeof makePayerPairOverrideSchema>>;
